import React, { useMemo, useState } from "react";
import {
  Briefcase,
  User,
  Split,
  Gauge as GaugeIcon,
  ShieldCheck,
  Lightbulb,
} from "lucide-react";
import { fmtMoney, fmtNumber } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import HireUsCTA from "@/components/HireUsCTA";
import EmailResultsCard from "@/components/EmailResultsCard";
import NudgeCard from "@/components/NudgeCard";

/* -------------------------------------------------------------------
   Q U E S T I O N S
   ------------------------------------------------------------------- */
const FREQUENCY = [
  { label: "Never", val: 0 },
  { label: "Sometimes", val: 30 },
  { label: "Most jobs", val: 70 },
  { label: "Every job", val: 100 },
];

const COUNTERFACTUAL = [
  { label: "Yes, same one", val: 0 },
  { label: "Cheaper version", val: 30 },
  { label: "Probably not", val: 70 },
  { label: "No way", val: 100 },
];

/* -------------------------------------------------------------------
   S C O R I N G
   ------------------------------------------------------------------- */
function scoreOf({ frequency, counterfactual, businessPct }) {
  // Weighted: how much weight does each input carry?
  const W_FREQ = 0.25;
  const W_CF = 0.2;
  const W_PCT = 0.55;
  const score =
    frequency * W_FREQ + counterfactual * W_CF + businessPct * W_PCT;
  return Math.max(0, Math.min(100, Math.round(score)));
}

function verdict(score) {
  if (score >= 80)
    return {
      key: "business",
      title: "Mostly business",
      tone: "primary",
      icon: Briefcase,
      blurb:
        "Looks like a genuine business expense. You can usually claim the lot.",
    };
  if (score >= 55)
    return {
      key: "business",
      title: "Leaning business",
      tone: "primary",
      icon: Briefcase,
      blurb:
        "More work than play. Claim the business portion — keep evidence of the split.",
    };
  if (score >= 35)
    return {
      key: "mixed",
      title: "Mixed-use",
      tone: "secondary",
      icon: Split,
      blurb:
        "It's both. Claim the business slice only — IRD will want to see how you worked it out.",
    };
  if (score >= 15)
    return {
      key: "personal",
      title: "Leaning personal",
      tone: "warning",
      icon: User,
      blurb:
        "Looks personal. A small business slice might be claimable if you can defend it.",
    };
  return {
    key: "personal",
    title: "Personal",
    tone: "warning",
    icon: User,
    blurb:
      "This is a personal expense. Don't claim it — IRD won't be impressed if they audit.",
  };
}

/* -------------------------------------------------------------------
   E X A M P L E S  (NZ-tradie flavoured)
   ------------------------------------------------------------------- */
const EXAMPLES = [
  { item: "Drill bits, saw blades, hand tools", typical: 100, note: "Pure work — claim all." },
  { item: "Work van — daily use", typical: 90, note: "Logbook ideal, otherwise 75–90%." },
  { item: "Mobile phone (one phone, mostly work calls)", typical: 70 },
  { item: "Internet at home (1 person works from home)", typical: 50 },
  { item: "Ute that's also the family car", typical: 65, note: "Needs logbook." },
  { item: "Client coffee / lunch", typical: 50, note: "50% IRD entertainment rule." },
  { item: "Boots + hi-vis", typical: 100, note: "Protective clothing, fully claimable." },
  { item: "Gym membership", typical: 5, note: "Not worth claiming — very hard to defend." },
  { item: "Subscription to a trade magazine", typical: 100 },
  { item: "TV / Netflix in the workshop", typical: 10, note: "Hard to defend." },
];

/* -------------------------------------------------------------------
   S P E E D O M E T E R
   ------------------------------------------------------------------- */
function Speedometer({ value, label, sublabel }) {
  // value 0-100 mapped to angle -90 (left) → +90 (right)
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const angle = -90 + (v / 100) * 180;
  // Sweep arc utility
  const R = 90;
  const cx = 110;
  const cy = 110;
  const polar = (deg) => {
    const r = ((deg - 90) * Math.PI) / 180;
    return { x: cx + R * Math.cos(r), y: cy + R * Math.sin(r) };
  };
  // Build three coloured arcs: 0-35 red, 35-65 amber, 65-100 gold
  const arc = (fromDeg, toDeg, color) => {
    const start = polar(-90 + (fromDeg / 100) * 180 + 90);
    const end = polar(-90 + (toDeg / 100) * 180 + 90);
    const largeArc = toDeg - fromDeg > 50 ? 1 : 0;
    return (
      <path
        d={`M ${start.x} ${start.y} A ${R} ${R} 0 ${largeArc} 1 ${end.x} ${end.y}`}
        stroke={color}
        strokeWidth="14"
        fill="none"
        strokeLinecap="butt"
      />
    );
  };

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 220 130" className="w-full max-w-[280px] h-auto">
        {/* Track */}
        <path
          d={`M 20 110 A ${R} ${R} 0 0 1 200 110`}
          stroke="var(--border)"
          strokeWidth="14"
          fill="none"
        />
        {/* Coloured zones */}
        {arc(0, 35, "var(--error)")}
        {arc(35, 65, "var(--secondary)")}
        {arc(65, 100, "var(--primary)")}

        {/* Tick marks */}
        {[0, 25, 50, 75, 100].map((t) => {
          const a = ((-90 + (t / 100) * 180) * Math.PI) / 180;
          const x1 = cx + (R - 10) * Math.cos(a);
          const y1 = cy + (R - 10) * Math.sin(a);
          const x2 = cx + (R + 6) * Math.cos(a);
          const y2 = cy + (R + 6) * Math.sin(a);
          return (
            <line
              key={t}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="var(--text)"
              strokeOpacity="0.4"
              strokeWidth="1.5"
            />
          );
        })}

        {/* Needle */}
        <g
          style={{
            transform: `rotate(${angle}deg)`,
            transformOrigin: `${cx}px ${cy}px`,
            transition: "transform 350ms cubic-bezier(0.2,0.8,0.2,1)",
          }}
        >
          <line
            x1={cx}
            y1={cy}
            x2={cx}
            y2={cy - R + 18}
            stroke="var(--text)"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx={cx} cy={cy} r="8" fill="var(--text)" />
          <circle cx={cx} cy={cy} r="3" fill="var(--primary)" />
        </g>

        {/* Labels */}
        <text
          x="20"
          y="125"
          fontFamily="JetBrains Mono"
          fontSize="9"
          fill="var(--muted)"
        >
          PERSONAL
        </text>
        <text
          x="200"
          y="125"
          fontFamily="JetBrains Mono"
          fontSize="9"
          fill="var(--muted)"
          textAnchor="end"
        >
          BUSINESS
        </text>
      </svg>
      <div className="text-center mt-2">
        <div
          data-testid="bp-score-value"
          className="font-display text-4xl text-[var(--primary)]"
        >
          {v}
          <span className="text-xl text-zinc-500">%</span>
        </div>
        <div className="label-mono mt-1">{label}</div>
        {sublabel && (
          <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{sublabel}</div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------
   M A I N
   ------------------------------------------------------------------- */
export default function BusinessPersonalCalc() {
  const { t } = useLang();
  const [frequency, setFrequency] = useState(70);
  const [counterfactual, setCounterfactual] = useState(70);
  const [businessPct, setBusinessPct] = useState(60);
  const [cost, setCost] = useState(500);

  const businessScore = useMemo(
    () => scoreOf({ frequency, counterfactual, businessPct }),
    [frequency, counterfactual, businessPct],
  );
  const personalScore = 100 - businessScore;
  const v = verdict(businessScore);
  const VerdictIcon = v.icon;

  const claimable = (cost * businessScore) / 100;
  const personal = cost - claimable;

  return (
    <div className="stagger-in">
      <div className="label-mono">/ {t("common.calculator", "CALCULATOR")} · 06</div>
      <h1 className="font-display tracking-tight mt-2">{t("expense.h1", "Business or Personal?")}</h1>
      <p className="text-zinc-400 mt-2 max-w-3xl">
        {t("expense.sub", "Not sure if an expense is for your business, just for you, or a bit of both? Answer 3 quick questions — we'll tell you what's likely deductible.")}
      </p>

      {/* GAUGES */}
      <div
        className="grid grid-cols-1 md:grid-cols-3 gap-0 mt-8 border border-[var(--border)] bg-[var(--surface)]"
        data-testid="bp-gauges"
      >
        <div className="p-6 border-b md:border-b-0 md:border-r border-[var(--border)]">
          <Speedometer
            value={businessScore}
            label="Business decision"
            sublabel="Claimable on your tax return"
          />
        </div>
        <div className="p-6 border-b md:border-b-0 md:border-r border-[var(--border)]">
          <Speedometer
            value={personalScore}
            label="Personal decision"
            sublabel="Out of your own pocket"
          />
        </div>
        <div className="p-6 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-[var(--primary)]/10 border border-[var(--primary)]/30 flex items-center justify-center mb-4">
            <VerdictIcon size={28} className="text-[var(--primary)]" />
          </div>
          <div
            className="font-display text-2xl"
            data-testid="bp-verdict-title"
          >
            {v.title}
          </div>
          <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{v.blurb}</p>
        </div>
      </div>

      {/* THE 3 QUESTIONS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 mt-8 border border-[var(--border)]">
        {/* Q1 */}
        <div className="p-4 sm:p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-[var(--border)]">
          <div className="label-mono">Question 1</div>
          <h3 className="font-display text-xl tracking-tight mt-2">
            How often do you use it for paid work?
          </h3>
          <div className="grid grid-cols-2 gap-2 mt-5">
            {FREQUENCY.map((opt) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => setFrequency(opt.val)}
                className={`px-3 py-3 font-mono text-xs uppercase tracking-wider border ${
                  frequency === opt.val
                    ? "bg-[var(--primary)] text-[var(--on-primary)] border-[var(--primary)]"
                    : "border-[var(--border)] text-zinc-300 hover:border-[var(--primary)]"
                }`}
                data-testid={`bp-q1-${opt.val}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Q2 */}
        <div className="p-4 sm:p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-[var(--border)]">
          <div className="label-mono">Question 2</div>
          <h3 className="font-display text-xl tracking-tight mt-2">
            Would you have bought it if you weren't running a business?
          </h3>
          <div className="grid grid-cols-2 gap-2 mt-5">
            {COUNTERFACTUAL.map((opt) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => setCounterfactual(opt.val)}
                className={`px-3 py-3 font-mono text-xs uppercase tracking-wider border ${
                  counterfactual === opt.val
                    ? "bg-[var(--primary)] text-[var(--on-primary)] border-[var(--primary)]"
                    : "border-[var(--border)] text-zinc-300 hover:border-[var(--primary)]"
                }`}
                data-testid={`bp-q2-${opt.val}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Q3 */}
        <div className="p-4 sm:p-6 md:p-8">
          <div className="label-mono">Question 3</div>
          <h3 className="font-display text-xl tracking-tight mt-2">
            Your best guess — what % is for business?
          </h3>
          <div className="mt-5">
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={businessPct}
              onChange={(e) => setBusinessPct(Number(e.target.value))}
              data-testid="bp-q3-slider"
              className="w-full accent-[var(--primary)]"
              style={{ height: "8px" }}
            />
            <div className="flex justify-between text-[10px] font-mono text-zinc-500 mt-1">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
            <div className="text-center mt-3">
              <span className="font-display text-3xl text-[var(--primary)]">
                {businessPct}%
              </span>
              <span className="text-xs font-mono text-zinc-500 ml-2">
                business use
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* DOLLAR IMPACT */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mt-8 border border-[var(--border)]">
        <div className="p-4 sm:p-6 md:p-8 border-b md:border-b-0 md:border-r border-[var(--border)]">
          <div className="label-mono mb-2">Item cost (NZD, incl. GST)</div>
          <input
            type="number"
            step="1"
            className="input-sharp text-2xl py-4"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            data-testid="bp-cost-input"
          />
          <div className="text-[10px] font-mono text-zinc-500 mt-2">
            What did this thing actually cost you?
          </div>
        </div>
        <div className="p-4 sm:p-6 md:p-8 border-b md:border-b-0 md:border-r border-[var(--border)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <Briefcase size={14} className="text-[var(--primary)]" />
            <div className="label-mono">Business slice</div>
          </div>
          <div
            className="font-display text-3xl mt-3 text-[var(--primary)]"
            data-testid="bp-business-claim"
          >
            {fmtMoney(claimable)}
          </div>
          <div className="text-xs font-mono text-zinc-400 mt-2">
            Claimable as a business expense
          </div>
        </div>
        <div className="p-4 sm:p-6 md:p-8 bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <User size={14} className="text-zinc-400" />
            <div className="label-mono">Personal slice</div>
          </div>
          <div
            className="font-display text-3xl mt-3 text-white"
            data-testid="bp-personal-portion"
          >
            {fmtMoney(personal)}
          </div>
          <div className="text-xs font-mono text-zinc-400 mt-2">
            Out of your own back pocket
          </div>
        </div>
      </div>

      {/* THE NEXUS RULE — plain English */}
      <div
        className="mt-8 border border-[var(--border)] p-4 sm:p-6 md:p-8 bg-[var(--surface)]"
        data-testid="bp-nexus-card"
      >
        <div className="flex items-start gap-3">
          <GaugeIcon
            size={20}
            className="text-[var(--primary)] shrink-0 mt-0.5"
          />
          <div>
            <div className="label-mono">The one word IRD cares about</div>
            <h3 className="font-display text-xl mt-1">
              Nexus —{" "}
              <span className="text-[var(--primary)]">does it actually help you earn?</span>
            </h3>
            <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
              <strong>Nexus</strong> is tax-agent speak for{" "}
              <em>"a clear connection between the expense and the money you earn."</em>{" "}
              In plain English: if buying it helps you do paid work, it has nexus
              and you can probably claim it. If it doesn't, you can't.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 text-sm">
              <div className="border-l-2 border-[var(--primary)] pl-3">
                <div className="label-mono text-[var(--primary)]">Strong nexus</div>
                <div className="text-zinc-300 mt-1">
                  Saw blade for the job today → 100% claim. No question.
                </div>
              </div>
              <div className="border-l-2 border-[var(--error)] pl-3">
                <div className="label-mono" style={{ color: "var(--error)" }}>
                  Weak / no nexus
                </div>
                <div className="text-zinc-300 mt-1">
                  Gym membership "to stay fit for work" → too thin a thread.
                  IRD will throw it out.
                </div>
              </div>
            </div>
            <p className="text-xs font-mono text-zinc-500 mt-4">
              Rule of thumb: if you'd struggle to explain the connection to your
              mate at smoko, you'll struggle to explain it to IRD too.
            </p>
          </div>
        </div>
      </div>

      {/* IRD TIPS */}
      <div className="mt-8 border border-[var(--border)] p-4 sm:p-6 md:p-8 bg-[var(--surface)]">
        <div className="flex items-start gap-3">
          <ShieldCheck
            size={20}
            className="text-[var(--primary)] shrink-0 mt-0.5"
          />
          <div>
            <div className="label-mono">If IRD ever asks</div>
            <h3 className="font-display text-xl mt-1">Show your working</h3>
            <ul className="mt-3 space-y-2 text-sm text-zinc-300 leading-relaxed">
              <li>
                <span className="text-[var(--primary)] font-mono">·</span> Keep
                receipts (GST invoices if you're registered).
              </li>
              <li>
                <span className="text-[var(--primary)] font-mono">·</span> For
                vehicles, run a 90-day logbook every 3 years (IRD-approved).
              </li>
              <li>
                <span className="text-[var(--primary)] font-mono">·</span>{" "}
                Mixed-use phones / internet: pick a fair % and stay consistent
                year to year.
              </li>
              <li>
                <span className="text-[var(--primary)] font-mono">·</span>{" "}
                Entertainment (food, drink, hospitality with clients) is
                generally only <strong>50% deductible</strong>.
              </li>
              <li>
                <span className="text-[var(--primary)] font-mono">·</span> If
                you're guessing, write it down — a defended estimate beats none
                at all.
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* EXAMPLES CHEAT SHEET */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb size={14} className="text-[var(--primary)]" />
          <div className="label-mono">Common tradie expenses</div>
        </div>
        <div className="border border-[var(--border)]" data-testid="bp-examples">
          <div className="grid grid-cols-12 gap-0 text-xs font-mono uppercase tracking-wider text-zinc-500 border-b border-[var(--border)]">
            <div className="col-span-7 p-3">Item</div>
            <div className="col-span-2 p-3 border-l border-[var(--border)]">
              Typical
            </div>
            <div className="col-span-3 p-3 border-l border-[var(--border)]">
              Note
            </div>
          </div>
          {EXAMPLES.map((ex, i) => (
            <div
              key={i}
              className="grid grid-cols-12 gap-0 text-sm border-b border-[var(--border)] last:border-b-0"
            >
              <div className="col-span-7 p-3 text-zinc-200">{ex.item}</div>
              <div className="col-span-2 p-3 border-l border-[var(--border)] font-mono text-[var(--primary)]">
                {fmtNumber(ex.typical, 0)}%
              </div>
              <div className="col-span-3 p-3 border-l border-[var(--border)] text-xs text-zinc-400">
                {ex.note || "—"}
              </div>
            </div>
          ))}
        </div>
      </div>

      <NudgeCard
        show={businessScore >= 35 && businessScore <= 65}
        tone="info"
        title="Mixed-use is where IRD audits trip people up"
        body="Picking a fair business % is half art, half evidence. Our $75/mo Growth plan reviews these calls every month so you're not making them alone."
        cta="Get expert eyes on this"
        campaign="bp-mixed-nudge"
      />

      <EmailResultsCard
        calculator="business-personal"
        payload={{
          frequency,
          counterfactual,
          business_pct_self: businessPct,
          business_score: businessScore,
          cost: Number(cost) || 0,
          claimable,
          personal,
          verdict: v.title,
        }}
        labelKey="email.expense.label"
        descKey="email.expense.desc"
      />

      <HireUsCTA
        campaign="business-personal-calc"
        headline="Not sure? We're sure for a living."
        subline="Determining what's deductible is literally our job. If you're guessing on more than a couple of expenses a year, an accountant probably pays for themselves."
      />
    </div>
  );
}
